import Route from '@ember/routing/route'
import { service } from '@ember/service'
import type { Log } from '@blakeelearning/log'
import type RouterService from '@ember/routing/router-service'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import type WritingLegendsSubmissionService from 'teacher-ui-client/services/writing-legends-submission'
import type { EWIActivity } from 'teacher-ui-client/subscription-type/writing-ideas/previews/route'
import type WritingLegendsCatalogueService from 'teacher-ui-client/services/writing-legends-catalogue'

interface Params extends Record<string, unknown> {
  activity_id: string
  student_id: string
  snapshot_id: string
}

export interface EWISubmission {
  score: number
  words: number
  reflection: string
  updated_date: number
  result_id: string
  answer_snapshots: {
    answer_text: string
    plain_text: string
  }[]
  legends_feedback: {
    target_word_count: number
  }[]
}

interface Student {
  first_name: string
  last_name: string
  username: string
  user_id: string
  gradePosition: number
}

export default class EWISubmissionRoute extends Route {
  @service writingLegendsSubmission!: WritingLegendsSubmissionService

  @service writingLegendsCatalogue!: WritingLegendsCatalogueService

  @service log!: Log

  @service router!: RouterService

  async model(params: Params) {
    const { subscriptionType } = this.modelFor('subscription-type') as ModelForSubscriptionTypeRoute
    // if its not writing subscription type, redirect to home
    if (subscriptionType !== SubscriptionType.Writing) {
      this.log.error('view writing page is only available for writing subscription type')
      void this.router.replaceWith('subscription-type.home')
      return { subscriptionType }
    }

    const [submission, activity, student] = await Promise.all([
      this.writingLegendsSubmission.fetchData<EWISubmission>(
        this.writingLegendsSubmission.buildSubmissionUrl(params.student_id, params.snapshot_id),
      ),
      this.writingLegendsCatalogue.fetchData<EWIActivity>(
        this.writingLegendsCatalogue.buildActivityUrl(params.activity_id),
      ),
      this.writingLegendsSubmission.fetchData<{ data: Student }>(
        this.writingLegendsSubmission.buildStudentProfileUrl(params.student_id),
      ),
    ])

    return {
      params,
      submission,
      activity,
      student: student?.data,
    }
  }
}
